import React, { useState, useEffect } from 'react';
import {
  Download, Upload, FileText, FileSpreadsheet, Archive,
  Calendar, Clock, CheckCircle2, AlertCircle, XCircle, RefreshCw,
  Settings, Filter, Search, Eye, EyeOff, Play, Pause, Square,
  RotateCcw, Trash2, Edit, Save, Plus, Minus, ChevronDown,
  ChevronRight, MoreHorizontal, X, Info, HelpCircle, ExternalLink,
  Copy, Bell, Mail, Phone, MessageSquare, Users, Ticket, Database,
  HardDrive, Network, Server, Globe, Shield, Zap, Target, Activity,
  TrendingUp, TrendingDown, BarChart3, PieChart, LineChart, Table,
  List, Grid, Columns, Rows, File, FileVideo, FileAudio, FileImage,
  FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch,
  FileSlash, FileWarning, FileQuestion, FileHeart, FileClock,
  FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart,
  FileLineChart, FileScissors, FileType, FileSymlink, FileStack,
  FileTree, FileBinary, FileCog, FileSettings, FileShield, FileLock,
  FileUnlock, FileKey, FileCertificate, FileSignature, FileVerified,
  FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag,
  FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat,
  FileMail, FilePhone, Timer, Star, Award, Crown, Trophy, Medal,
  Gift, Coffee, Pizza, Cake, Music, Camera, Video, Share, Bookmark,
  Flag, Home, Menu, ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
  ChevronUp, ChevronLeft, MoreVertical, Lock, Unlock, Key
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const DataExportTools = () => {
  const [exports, setExports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentExport, setCurrentExport] = useState(null);
  const [exportForm, setExportForm] = useState({
    name: '',
    description: '',
    dataSource: '',
    format: 'csv',
    filters: [],
    fields: [],
    schedule: null,
    recipients: [],
    compression: false,
    encryption: false
  });
  const [filterInput, setFilterInput] = useState('');
  const [fieldInput, setFieldInput] = useState('');
  const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => {
    loadExportData();
  }, []);

  const loadExportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [exportsResponse, templatesResponse, schedulesResponse] = await Promise.all([
        new AdminService().getDataExports(),
        new AdminService().getExportTemplates(),
        new AdminService().getExportSchedules()
      ]);

      if (exportsResponse.success) {
        setExports(exportsResponse.data);
      }
      if (templatesResponse.success) {
        setTemplates(templatesResponse.data);
      }
      if (schedulesResponse.success) {
        setSchedules(schedulesResponse.data);
      }
    } catch (err) {
      setError('Failed to fetch export data.');
      console.error('Error fetching export data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExportForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddFilter = () => {
    if (filterInput.trim()) {
      setExportForm(prev => ({
        ...prev,
        filters: [...prev.filters, { field: filterInput.trim(), operator: 'equals', value: '' }]
      }));
      setFilterInput('');
    }
  };

  const handleUpdateFilter = (index, field, value) => {
    const newFilters = [...exportForm.filters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    setExportForm(prev => ({ ...prev, filters: newFilters }));
  };

  const handleRemoveFilter = (index) => {
    setExportForm(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const handleAddField = () => {
    if (fieldInput.trim()) {
      setExportForm(prev => ({
        ...prev,
        fields: [...prev.fields, fieldInput.trim()]
      }));
      setFieldInput('');
    }
  };

  const handleRemoveField = (index) => {
    setExportForm(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleAddRecipient = () => {
    if (recipientInput.trim() && recipientInput.includes('@')) {
      setExportForm(prev => ({
        ...prev,
        recipients: [...prev.recipients, recipientInput.trim()]
      }));
      setRecipientInput('');
    }
  };

  const handleRemoveRecipient = (index) => {
    setExportForm(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const handleScheduleChange = (field, value) => {
    setExportForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value
      }
    }));
  };

  const openCreateModal = () => {
    setCurrentExport(null);
    setExportForm({
      name: '',
      description: '',
      dataSource: '',
      format: 'csv',
      filters: [],
      fields: [],
      schedule: null,
      recipients: [],
      compression: false,
      encryption: false
    });
    setIsModalOpen(true);
  };

  const openEditModal = (exportItem) => {
    setCurrentExport(exportItem);
    setExportForm({
      name: exportItem.name,
      description: exportItem.description,
      dataSource: exportItem.dataSource,
      format: exportItem.format,
      filters: exportItem.filters,
      fields: exportItem.fields,
      schedule: exportItem.schedule,
      recipients: exportItem.recipients,
      compression: exportItem.compression,
      encryption: exportItem.encryption
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentExport(null);
    setExportForm({
      name: '',
      description: '',
      dataSource: '',
      format: 'csv',
      filters: [],
      fields: [],
      schedule: null,
      recipients: [],
      compression: false,
      encryption: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (currentExport) {
        response = await new AdminService().updateDataExport(currentExport.id, exportForm);
      } else {
        response = await new AdminService().createDataExport(exportForm);
      }

      if (response.success) {
        alert(`Export ${currentExport ? 'updated' : 'created'} successfully!`);
        closeModal();
        loadExportData();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(`Failed to ${currentExport ? 'update' : 'create'} export.`);
      console.error(`Error ${currentExport ? 'updating' : 'creating'} export:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (exportId) => {
    if (window.confirm('Are you sure you want to delete this export?')) {
      setLoading(true);
      try {
        const response = await new AdminService().deleteDataExport(exportId);
        if (response.success) {
          alert('Export deleted successfully!');
          loadExportData();
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Failed to delete export.');
        console.error('Error deleting export:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRunExport = async (exportId) => {
    setLoading(true);
    try {
      const response = await new AdminService().runDataExport(exportId);
      if (response.success) {
        alert(`Export "${exportId}" executed successfully! File: ${response.data.filename}`);
        loadExportData();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to run export.');
      console.error('Error running export:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = (template) => {
    setExportForm({
      name: template.name + ' (Copy)',
      description: template.description,
      dataSource: template.dataSource,
      format: template.format,
      filters: template.filters,
      fields: template.fields,
      schedule: null,
      recipients: [],
      compression: template.compression,
      encryption: template.encryption
    });
    alert(`Template "${template.name}" applied. Customize and save your export.`);
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'text-green-600 bg-green-100',
      running: 'text-blue-600 bg-blue-100',
      failed: 'text-red-600 bg-red-100',
      scheduled: 'text-yellow-600 bg-yellow-100',
      paused: 'text-gray-600 bg-gray-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getFormatIcon = (format) => {
    const icons = {
      csv: FileSpreadsheet,
      xlsx: FileSpreadsheet,
      pdf: FileText,
      json: FileText,
      xml: FileText,
      zip: Archive
    };
    const IconComponent = icons[format] || FileText;
    return <IconComponent className="w-5 h-5" />;
  };

  if (loading && exports.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Export Tools</h1>
          <p className="text-gray-600">Export data for external analysis and reporting</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Export
        </button>
      </div>

      {/* Export Templates */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Export Templates</h3>
          <p className="text-gray-600">Pre-configured export templates for common use cases</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map(template => (
              <div key={template.id} className="bg-gray-50 p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    {getFormatIcon(template.format)}
                    <h4 className="font-medium text-gray-900 ml-2">{template.name}</h4>
                  </div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                    {template.format.toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    {template.dataSource}
                  </span>
                  {template.compression && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                      Compressed
                    </span>
                  )}
                  {template.encryption && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                      Encrypted
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleApplyTemplate(template)}
                  className="w-full flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Use Template
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export Management */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Export Management</h3>
          <p className="text-gray-600">Manage your data exports and schedules</p>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Export Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Source
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Format
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Run
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exports.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No exports created yet.</td>
                  </tr>
                ) : (
                  exports.map((exportItem) => (
                    <tr key={exportItem.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{exportItem.name}</div>
                        <div className="text-sm text-gray-500">{exportItem.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {exportItem.dataSource}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getFormatIcon(exportItem.format)}
                          <span className="ml-2 text-sm text-gray-900">{exportItem.format.toUpperCase()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(exportItem.status)}`}>
                          {exportItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {exportItem.lastRun ? new Date(exportItem.lastRun).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRunExport(exportItem.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Run Export"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(exportItem)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit Export"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(exportItem.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Export"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Export Schedules */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Export Schedules</h3>
          <p className="text-gray-600">Automated export schedules and monitoring</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedules.map(schedule => (
              <div key={schedule.id} className="bg-gray-50 p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(schedule.status)}`}>
                    {schedule.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{schedule.frequency}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{schedule.time}</span>
                  </div>
                  <div className="flex items-center">
                    <Database className="w-4 h-4 mr-2" />
                    <span>{schedule.dataSource}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Next run: {new Date(schedule.nextRun).toLocaleString()}
                  </span>
                  <div className="flex items-center space-x-1">
                    <button className="text-blue-600 hover:text-blue-900" title="Edit Schedule">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900" title="Delete Schedule">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create/Edit Export Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {currentExport ? 'Edit Export' : 'Create New Export'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Export Name</label>
                    <input
                      type="text"
                      name="name"
                      value={exportForm.name}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data Source</label>
                    <select
                      name="dataSource"
                      value={exportForm.dataSource}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Data Source</option>
                      <option value="tickets">Tickets</option>
                      <option value="users">Users</option>
                      <option value="agents">Agents</option>
                      <option value="departments">Departments</option>
                      <option value="knowledge">Knowledge Base</option>
                      <option value="reports">Reports</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={exportForm.description}
                    onChange={handleFormChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Export Format</label>
                    <select
                      name="format"
                      value={exportForm.format}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="csv">CSV</option>
                      <option value="xlsx">Excel (XLSX)</option>
                      <option value="pdf">PDF</option>
                      <option value="json">JSON</option>
                      <option value="xml">XML</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-4 mt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="compression"
                        checked={exportForm.compression}
                        onChange={handleFormChange}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Compress</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="encryption"
                        checked={exportForm.encryption}
                        onChange={handleFormChange}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Encrypt</span>
                    </label>
                  </div>
                </div>

                {/* Filters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Filters</label>
                  <div className="mt-1 space-y-2">
                    {exportForm.filters.map((filter, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={filter.field}
                          onChange={(e) => handleUpdateFilter(index, 'field', e.target.value)}
                          placeholder="Field"
                          className="flex-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <select
                          value={filter.operator}
                          onChange={(e) => handleUpdateFilter(index, 'operator', e.target.value)}
                          className="border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="equals">Equals</option>
                          <option value="contains">Contains</option>
                          <option value="greater_than">Greater Than</option>
                          <option value="less_than">Less Than</option>
                        </select>
                        <input
                          type="text"
                          value={filter.value}
                          onChange={(e) => handleUpdateFilter(index, 'value', e.target.value)}
                          placeholder="Value"
                          className="flex-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveFilter(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={filterInput}
                        onChange={(e) => setFilterInput(e.target.value)}
                        placeholder="Add filter field"
                        className="flex-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddFilter}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fields to Export</label>
                  <div className="mt-1 space-y-2">
                    {exportForm.fields.map((field, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-900">{field}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveField(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={fieldInput}
                        onChange={(e) => setFieldInput(e.target.value)}
                        placeholder="Add field"
                        className="flex-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddField}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recipients */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Recipients</label>
                  <div className="mt-1 space-y-2">
                    {exportForm.recipients.map((recipient, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-900">{recipient}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={recipientInput}
                        onChange={(e) => setRecipientInput(e.target.value)}
                        placeholder="Add email recipient"
                        className="flex-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddRecipient}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {currentExport ? 'Update Export' : 'Create Export'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataExportTools;
